/**
 * World Cup War Room — pure derivation + lightweight session telemetry.
 *
 * Goals:
 *  - Read-only / in-memory. No new realtime, no DB storms.
 *  - Detects viral spikes from short rolling history of already-polled signals.
 *  - Exposes counters that other engines push into (cinematic, sponsor, showcase, clips).
 */

export type SpikeKind =
  | "hype_explosion"
  | "nation_dominance"
  | "share_burst"
  | "creator_surge"
  | "viral_spike";

export type SpikeAlert = {
  id: string;
  kind: SpikeKind;
  label: string;
  detail: string;
  intensity: number; // 0..1
  createdAt: number;
  emoji: string;
};

type Counter = {
  total: number;
  lastMinute: number;
  lastMinuteResetAt: number;
};

const counters: Record<string, Counter> = {
  shares: emptyCounter(),
  clipExports: emptyCounter(),
  showcaseStarts: emptyCounter(),
  cinematicMoments: emptyCounter(),
  sponsorImpressions: emptyCounter(),
  sponsorInteractions: emptyCounter(),
  pollVotes: emptyCounter(),
  referrals: emptyCounter(),
  creatorViews: emptyCounter(),
  challengeProgress: emptyCounter(),
  // PR-38B-E — push delivery is tracked separately from sponsor metrics so
  // billable impressions are never inflated by retention touches.
  pushTouches: emptyCounter(),
};

function emptyCounter(): Counter {
  return { total: 0, lastMinute: 0, lastMinuteResetAt: Date.now() };
}

function rollIfNeeded(c: Counter) {
  const now = Date.now();
  if (now - c.lastMinuteResetAt > 60_000) {
    c.lastMinute = 0;
    c.lastMinuteResetAt = now;
  }
}

export function recordWarRoomEvent(kind: keyof typeof counters, delta = 1) {
  const c = counters[kind];
  if (!c) return;
  rollIfNeeded(c);
  c.total += delta;
  c.lastMinute += delta;
}

export function snapshotCounters() {
  const out: Record<string, Counter> = {};
  for (const [k, v] of Object.entries(counters)) {
    rollIfNeeded(v);
    out[k] = { ...v };
  }
  return out;
}

// --- Rolling spike detection on already-cached pulse/rivalry ---

type PulseHistoryPoint = { t: number; fans: number; hype: number };
const pulseHistory: PulseHistoryPoint[] = [];
const MAX_HISTORY = 24; // ~6 minutes at 15s polling

type NationHistoryPoint = { t: number; top: string | null; share: number };
const nationHistory: NationHistoryPoint[] = [];

const recentAlerts: SpikeAlert[] = [];
const ALERT_TTL = 5 * 60_000;
const ALERT_COOLDOWN: Record<SpikeKind, number> = {
  hype_explosion: 60_000,
  nation_dominance: 90_000,
  share_burst: 45_000,
  creator_surge: 60_000,
  viral_spike: 30_000,
};
const lastAlertAt: Partial<Record<SpikeKind, number>> = {};

function pushAlert(a: Omit<SpikeAlert, "id" | "createdAt">) {
  const now = Date.now();
  const cooldown = ALERT_COOLDOWN[a.kind];
  if (now - (lastAlertAt[a.kind] ?? 0) < cooldown) return null;
  lastAlertAt[a.kind] = now;
  const alert: SpikeAlert = {
    ...a,
    id: `${a.kind}-${now}`,
    createdAt: now,
  };
  recentAlerts.unshift(alert);
  while (recentAlerts.length > 12) recentAlerts.pop();
  return alert;
}

export function getRecentAlerts(): SpikeAlert[] {
  const now = Date.now();
  return recentAlerts.filter((a) => now - a.createdAt < ALERT_TTL);
}

export type WarRoomTick = {
  activeFans: number;
  hypeBurst: number;
  topNation: string | null;
  topNationShare: number;
  alerts: SpikeAlert[];
};

export function tickWarRoom(input: {
  activeFans: number;
  hypeBurst: number;
  topNation: string | null;
  topNationShare: number;
}): WarRoomTick {
  const now = Date.now();
  pulseHistory.push({ t: now, fans: input.activeFans, hype: input.hypeBurst });
  while (pulseHistory.length > MAX_HISTORY) pulseHistory.shift();
  nationHistory.push({ t: now, top: input.topNation, share: input.topNationShare });
  while (nationHistory.length > MAX_HISTORY) nationHistory.shift();

  // Baselines
  const prev = pulseHistory.slice(0, Math.max(0, pulseHistory.length - 3));
  const avgHype = prev.length
    ? prev.reduce((a, p) => a + p.hype, 0) / prev.length
    : input.hypeBurst;
  const avgFans = prev.length
    ? prev.reduce((a, p) => a + p.fans, 0) / prev.length
    : input.activeFans;

  const triggered: SpikeAlert[] = [];

  if (avgHype > 0 && input.hypeBurst > avgHype * 1.8 && input.hypeBurst > 25) {
    const a = pushAlert({
      kind: "hype_explosion",
      label: "🔥 Hype mundial explotando",
      detail: `+${Math.round(((input.hypeBurst - avgHype) / Math.max(1, avgHype)) * 100)}% vs baseline`,
      intensity: Math.min(1, input.hypeBurst / Math.max(1, avgHype * 3)),
      emoji: "🔥",
    });
    if (a) triggered.push(a);
  }

  if (avgFans > 0 && input.activeFans > avgFans * 1.6 && input.activeFans > 200) {
    const a = pushAlert({
      kind: "viral_spike",
      label: "💥 Viral spike detectado",
      detail: `${input.activeFans.toLocaleString()} fans activos`,
      intensity: Math.min(1, input.activeFans / Math.max(1, avgFans * 3)),
      emoji: "💥",
    });
    if (a) triggered.push(a);
  }

  if (input.topNation && input.topNationShare >= 0.55) {
    const a = pushAlert({
      kind: "nation_dominance",
      label: `⚠️ ${input.topNation} domina la conversación`,
      detail: `${Math.round(input.topNationShare * 100)}% del hype global`,
      intensity: input.topNationShare,
      emoji: "⚠️",
    });
    if (a) triggered.push(a);
  }

  // share burst from local counters
  const shares = counters.shares;
  rollIfNeeded(shares);
  if (shares.lastMinute >= 8) {
    const a = pushAlert({
      kind: "share_burst",
      label: "📣 Shares explotando",
      detail: `${shares.lastMinute} shares / min`,
      intensity: Math.min(1, shares.lastMinute / 30),
      emoji: "📣",
    });
    if (a) triggered.push(a);
  }

  const creator = counters.clipExports;
  rollIfNeeded(creator);
  if (creator.lastMinute >= 3 || counters.showcaseStarts.lastMinute >= 2) {
    const a = pushAlert({
      kind: "creator_surge",
      label: "🎬 Creators amplificando",
      detail: `${creator.lastMinute} clips · ${counters.showcaseStarts.lastMinute} showcases`,
      intensity: Math.min(1, (creator.lastMinute + counters.showcaseStarts.lastMinute) / 8),
      emoji: "🎬",
    });
    if (a) triggered.push(a);
  }

  return {
    activeFans: input.activeFans,
    hypeBurst: input.hypeBurst,
    topNation: input.topNation,
    topNationShare: input.topNationShare,
    alerts: triggered,
  };
}
